// useAnalysis — drives the five-agent paper analysis endpoint.
//
// Subscribes to the SSE stream from POST /api/papers/:id/analyze and exposes
// agents (id → { content, model, error, done }), status, and a `run()`
// trigger. Saving the result to a note is a separate call.

import { useCallback, useRef, useState } from 'react';

const INITIAL_AGENTS = {
  summary:        { id: 'summary',        content: '', done: false },
  critique:       { id: 'critique',       content: '', done: false },
  implementation: { id: 'implementation', content: '', done: false },
  related:        { id: 'related',        content: '', done: false },
  synthesis:      { id: 'synthesis',      content: '', done: false },
};

export const AGENT_LABELS = {
  summary:        { ko: '요약',       en: 'Summary' },
  critique:       { ko: '비평',       en: 'Critique' },
  implementation: { ko: '구현 분석',  en: 'Implementation' },
  related:        { ko: '관련 연구',  en: 'Related work' },
  synthesis:      { ko: '종합 보고',  en: 'Synthesis' },
};

export function useAnalysis(paperId) {
  const [agents, setAgents] = useState(INITIAL_AGENTS);
  const [status, setStatus] = useState('idle');    // idle | running | done | error
  const [error, setError]   = useState(null);
  const abortRef = useRef(null);

  const run = useCallback(async () => {
    if (!paperId) return;
    if (abortRef.current) abortRef.current.abort();
    setAgents(INITIAL_AGENTS);
    setError(null);
    setStatus('running');

    const ctl = new AbortController();
    abortRef.current = ctl;

    let res;
    try {
      res = await fetch(`/api/papers/${encodeURIComponent(paperId)}/analyze`, {
        method: 'POST',
        credentials: 'same-origin',
        signal: ctl.signal,
      });
    } catch (e) {
      setStatus('error');
      setError(e);
      return;
    }
    if (!res.ok || !res.body) {
      setStatus('error');
      setError(new Error(`analyze ${res.status}`));
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let nl;
        while ((nl = buf.indexOf('\n\n')) !== -1) {
          const frame = buf.slice(0, nl);
          buf = buf.slice(nl + 2);
          const payload = parseSseFrame(frame);
          if (!payload) continue;
          handleEvent(payload, setAgents, setStatus, setError);
        }
      }
    } catch (e) {
      if (e?.name !== 'AbortError') {
        setStatus('error');
        setError(e);
      }
    } finally {
      abortRef.current = null;
    }
  }, [paperId]);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    setStatus('idle');
  }, []);

  return { agents, status, error, run, stop };
}

function handleEvent(payload, setAgents, setStatus, setError) {
  if (payload.kind === 'agent' && payload.agent?.id) {
    const a = payload.agent;
    setAgents((prev) => ({
      ...prev,
      [a.id]: {
        id: a.id,
        content: a.content || '',
        model: a.model,
        error: a.error || null,
        done: true,
      },
    }));
  } else if (payload.kind === 'done') {
    setStatus('done');
  } else if (payload.kind === 'error') {
    setStatus('error');
    setError(new Error(payload.message || 'analysis failed'));
  }
}

function parseSseFrame(frame) {
  const dataLines = [];
  for (const line of frame.split('\n')) {
    if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
  }
  if (dataLines.length === 0) return null;
  try { return JSON.parse(dataLines.join('\n')); } catch { return null; }
}
